import React, { useState } from "react";
import { useHistory } from "react-router-dom";

import Box from "components/common/Box";
import Text from "components/common/Text";
import Stack from "components/common/Stack";
import IconButton from "components/common/IconButton";
import Button from "components/common/Button";
import AllInOneFooter from "components/allInOneDashboard/AllInOneFooter";
import CircularProgress from "@mui/material/CircularProgress";
import ArrowBackOutlinedIcon from "@mui/icons-material/ArrowBackOutlined";
import MarketPulseHistoricalCard from "components/allInOneDashboard/MarketPulse/MarketPulseHistoricalCard";
import MarketPulseDetailCard from "components/allInOneDashboard/MarketPulse/MarketPulseDetailCard";
import { UNIFIED_DASHBOARD_PATH } from "components/allInOneDashboard/constants/productRouteMap";
import { getPulseImageCardTitle } from "utils/normalizeMarketPulse";
import { useFetch } from "hooks/useFetch";

const PAGE_PADDING = { xs: 2, md: 4, lg: 7.5 };

const renderPulseDetail = (pulse, { previewOnly = false } = {}) => {
  if (!pulse) return null;

  const duration = pulse.duration || "5 min";
  const audioUrl = pulse.audios?.[0]?.url || "";
  const images = pulse.images || [];

  if (images.length > 0) {
    const imagesToShow = previewOnly ? [images[0]] : images;

    return imagesToShow.map((image, index) => (
      <MarketPulseDetailCard
        key={image.id || `${pulse.id}-${index}`}
        title={getPulseImageCardTitle(pulse, image, index)}
        duration={duration}
        audioUrl={audioUrl}
        image={image}
        showAudio={index === 0}
      />
    ));
  }

  return (
    <Box
      sx={{
        width: "100%",
        borderRadius: { xs: "14px", md: "16px" },
        backgroundColor: "#FFFFFF",
        border: "1px solid #E8ECF0",
        boxShadow: "0 1px 6px rgba(15, 23, 42, 0.04)",
        p: { xs: 2, sm: 2.5, md: 3 },
        mb: 2,
      }}
    >
      <Text
        sx={{
          fontWeight: 700,
          fontSize: "18px",
          color: "#111827",
          mb: 2,
        }}
      >
        {pulse.title}
      </Text>
      {audioUrl ? (
        <Box
          component="audio"
          controls
          src={audioUrl}
          sx={{ width: "100%", height: 40, mb: 2 }}
        />
      ) : null}
      {pulse.description ? (
        <Text sx={{ color: "#6B7280", fontSize: "14px", lineHeight: 1.6 }}>
          {pulse.description}
        </Text>
      ) : null}
    </Box>
  );
};

const MarketPulseHubContainer = () => {
  const history = useHistory();
  const [detailPulse, setDetailPulse] = useState(null);
  const {
    pulses,
    loading,
    loadingMore,
    error,
    hasMore,
    nextCursor,
    refetch,
    loadMore,
  } = useFetch({ type: "historical" });

  const latestPulse = pulses[0] || null;
  const isDetailView = Boolean(detailPulse);

  const handleOpenPulse = (pulse) => {
    setDetailPulse(pulse);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleBack = () => {
    if (isDetailView) {
      setDetailPulse(null);
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    history.push(UNIFIED_DASHBOARD_PATH);
  };

  return (
    <Box
      sx={{ width: "100%", minHeight: "calc(100vh - 120px)", bgcolor: "#FFFFFF" }}
    >
      <Box
        sx={{
          width: "100%",
          borderRadius: 0,
          borderBottomLeftRadius: { xs: "20px", md: "24px" },
          borderBottomRightRadius: { xs: "20px", md: "24px" },
          background: "linear-gradient(180deg, #FFFFFF 0%, #F9FAFB 100%)",
          px: PAGE_PADDING,
          py: { xs: 2.5, sm: 3.5, md: 4 },
          mb: { xs: 2.5, md: 3 },
        }}
      >
        <Box
          sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, mb: 1.25 }}
        >
          <IconButton
            onClick={handleBack}
            aria-label={
              isDetailView ? "Back to market pulse list" : "Back to explore"
            }
            sx={{
              width: 40,
              height: 40,
              border: "1px solid rgba(255, 255, 255, 0.9)",
              borderRadius: "12px",
              backgroundColor: "#FFFFFF",
              boxShadow: "0 1px 4px rgba(0, 0, 0, 0.06)",
              flexShrink: 0,
              "&:hover": { backgroundColor: "#F9FAFB" },
            }}
          >
            <ArrowBackOutlinedIcon sx={{ fontSize: 20, color: "#374151" }} />
          </IconButton>

          <Box>
            <Text
              sx={{
                fontSize: { xs: "26px", sm: "32px", md: "36px" },
                fontWeight: 700,
                color: "#0B1F4D",
                lineHeight: 1.15,
              }}
            >
              Market Pulse
            </Text>
            <Text
              sx={{
                fontSize: { xs: "17px", sm: "20px", md: "22px" },
                fontWeight: 700,
                color: "#111827",
                mt: 0.5,
              }}
            >
              {isDetailView
                ? detailPulse.title || detailPulse.market || "Pre-Market Commentary"
                : "Pre-Market Commentary"}
            </Text>
          </Box>
        </Box>

        {!isDetailView ? (
          <Text
            sx={{
              fontSize: { xs: "13px", sm: "14px" },
              color: "#9CA3AF",
              pl: { xs: 0, sm: 6.5 },
              maxWidth: 720,
            }}
          >
            Daily audio briefings + charts + notes. Open any session to play.
          </Text>
        ) : null}
      </Box>

      <Box sx={{ px: PAGE_PADDING, pb: 4 }}>
        {loading && pulses.length === 0 ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              py: 8,
            }}
          >
            <CircularProgress size={32} sx={{ color: "#122DB2" }} />
          </Box>
        ) : error && pulses.length === 0 ? (
          <Stack
            spacing={1.5}
            sx={{ alignItems: "center", justifyContent: "center", py: 6 }}
          >
            <Text sx={{ color: "#6B7280", textAlign: "center" }}>
              Couldn&apos;t load market pulse. Please try again.
            </Text>
            <Button
              variant="outlined"
              onClick={refetch}
              sx={{ textTransform: "none" }}
            >
              Retry
            </Button>
          </Stack>
        ) : pulses.length === 0 ? (
          <Text sx={{ color: "#6B7280", textAlign: "center", py: 6 }}>
            No historical market pulse available yet.
          </Text>
        ) : isDetailView ? (
          renderPulseDetail(detailPulse)
        ) : (
          <>
            {latestPulse ? (
              <Box
                onClick={() => handleOpenPulse(latestPulse)}
                role="button"
                tabIndex={0}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    handleOpenPulse(latestPulse);
                  }
                }}
                sx={{
                  cursor: "pointer",
                  mb: 0.5,
                  "&:hover": { opacity: 0.98 },
                }}
              >
                {renderPulseDetail(latestPulse, { previewOnly: true })}
              </Box>
            ) : null}

            <Stack spacing={1.5} sx={{ width: "100%" }}>
              {pulses.map((pulse, index) => (
                <MarketPulseHistoricalCard
                  key={`${pulse.id || pulse.title}-${pulse.rawDate}-${index}`}
                  pulse={pulse}
                  isNew={index === 0}
                  onClick={() => handleOpenPulse(pulse)}
                />
              ))}
            </Stack>

            {hasMore && nextCursor ? (
              <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
                <Box
                  component="button"
                  type="button"
                  onClick={loadMore}
                  disabled={loadingMore}
                  sx={{
                    px: 3,
                    py: 1.25,
                    borderRadius: "12px",
                    border: "1px solid #E5E7EB",
                    bgcolor: "#fff",
                    cursor: loadingMore ? "default" : "pointer",
                    fontSize: 14,
                    fontWeight: 600,
                    color: "#111827",
                    fontFamily: "inherit",
                    opacity: loadingMore ? 0.6 : 1,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    "&:hover": {
                      bgcolor: loadingMore ? "#fff" : "#F9FAFB",
                    },
                  }}
                >
                  {loadingMore ? (
                    <CircularProgress size={18} sx={{ color: "#122DB2" }} />
                  ) : (
                    "Load more"
                  )}
                </Box>
              </Box>
            ) : null}
          </>
        )}
      </Box>

      <Box sx={{ px: PAGE_PADDING, pb: { xs: 2, md: 3 } }}>
        <Text
          sx={{
            fontSize: { xs: "14px", sm: "15px" },
            lineHeight: 1.55,
            color: "#9CA3AF",
            fontWeight: 400,
          }}
        >
          <Box
            component="span"
            sx={{ fontWeight: 700, color: "#6B7280" }}
          >
            Disclaimer:
          </Box>{" "}
          The information, data, charts and company references
          presented in this article are compiled from publicly available sources
          believed to be reliable. While reasonable efforts have been made to
          ensure accuracy, Jarvis Invest does not guarantee the completeness,
          accuracy or timeliness of the information. This content is intended
          solely for educational and informational purposes and should not be
          construed as investment, financial or trading advice. Investments in
          securities are subject to market risks. Please conduct your own
          research or consult a SEBI Registered Investment Advisor before making
          any investment decision. Jarvis Invest is a SEBI Registered Investment
          Adviser (Registration No. INA000013235). Past performance is not
          indicative of future results.
        </Text>
      </Box>

      <AllInOneFooter />
    </Box>
  );
};

export default MarketPulseHubContainer;
